/** @jsxImportSource @emotion/react */
import { useId, useMemo } from "react";
import Alert from "components/common/Alert";
import { URL_KEYS } from "constants/dashboard";
import Pagination from "components/common/Pagination";
import parseErrorMessage from "helpers/parseErrorMessage";
import useEvent from "hooks/useEvent";
import useRouterSearchParams from "hooks/useRouterSearchParams";
import usePaginationSortSearchQueryParams from "hooks/usePaginationSortSearchQueryParams";
import InsightsTable from "tables/InsightsTable";
import useGetTagsList from "hookQueries/useGetTagsList";
import useGetInsights from "hookQueries/useGetInsights";
import { EMPTY_ARRAY } from "constants/common";
import { paginationWrapperCss } from "./index.css";
import Layout from "./Layout";

const TITLE = "Marvelous Insights";

export default function InsightsTableContainer() {
  const [searchParams, setSearchParams] = useRouterSearchParams();
  const urlParams = usePaginationSortSearchQueryParams();

  const perPage = Number(urlParams.perPage);
  const page = Number(urlParams.page) || 0;
  const tagsString = searchParams.get("tags");
  const tableId = useId();

  const { data: tagsData } = useGetTagsList();

  const queryParams = useMemo(() => {
    const tagsArray = typeof tagsString === "string" ? tagsString.split("|||") : undefined;
    return {
      ...urlParams,
      page,
      perPage,
      partnerId: searchParams.get("partnerId") || undefined,
      brandIds: searchParams.get("brandIds") || undefined,
      excludeBrandIds: searchParams.get("excludeBrandIds") || undefined,
      categoryId: searchParams.get("categoryId") || undefined,
      keywords: searchParams.get("keywords") || undefined,
      sentiments: searchParams.get("sentiments") || undefined,
      tags: tagsArray,
    };
  }, [urlParams, searchParams, tagsString, perPage, page]);

  const { isFetching, isLoading, error, data } = useGetInsights({ queryParams });
  const totalResults = data?.total || 0;
  const lastPage = Math.max(0, Math.ceil(totalResults / perPage) - 1);
  const insights = data?.data ?? EMPTY_ARRAY;

  const onChangePerPageHandler = useEvent((value) => {
    const params = new URLSearchParams(searchParams);
    params.set(URL_KEYS.perPage, value);
    params.delete(URL_KEYS.page);
    setSearchParams(params);
  });

  const onChangePage = useEvent((nextPage) => {
    const params = new URLSearchParams(searchParams);
    params.set(URL_KEYS.page, nextPage);
    setSearchParams(params);
    const element = document.getElementById(tableId);

    element?.scrollIntoView();
  });

  if (error) {
    return (
      <Layout>
        <div>
          <Alert title={TITLE} message={parseErrorMessage(error)} />
        </div>
      </Layout>
    );
  }

  if (!isLoading && !data?.total) {
    return (
      <Layout>
        <div>
          <Alert title={TITLE} type="info" message="no data" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout isFetching={isFetching} isLoading={isLoading} data={insights}>
      <InsightsTable
        perPage={perPage}
        tableId={tableId}
        isFetching={isFetching}
        isLoading={isLoading}
        data={insights}
        total={data?.total}
        allTags={tagsData}
      />

      <div css={paginationWrapperCss} className="flex-j-e align-self-stretch">
        <Pagination
          currentPage={page}
          lastPage={lastPage}
          perPage={perPage}
          onChangePerPage={onChangePerPageHandler}
          onChangePage={onChangePage}
          disabledJumpToPage={isLoading}
        />
      </div>
    </Layout>
  );
}
